<template>
  <v-card
    class="api-manager"
    elevation="2"
    :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'"
  >
    <v-card-title class="text-h6">
      <v-icon class="mr-2">mdi-api</v-icon>
      {{ $t("apiManager.title") }}
    </v-card-title>

    <v-card-text :class="{ 'rtl-content': $i18n.locale === 'he' }" class="pa-3">
      <v-row>
        <v-col cols="12" md="6">
          <ShapefileManager
            :loadingShapefile="loadingShapefile"
            :loadingVersion="loadingVersion"
            :shapefileRequestId="shapefileRequestId"
            :version="shapefileVersion"
            @request-shapefile-content="requestShapefileContentHandler"
            @update-shapefile-version="updateShapefileVersionHandler"
          >
            <template #response>
              <!-- Place shapefile response display here if needed -->
            </template>
          </ShapefileManager>
        </v-col>
        <v-col cols="12" md="6">
          <HistoryManager
            :loadingHistory="loadingHistory"
            :historyRequestId="historyRequestId"
            @request-history="requestHistoryHandler"
          >
            <template #response>
              <!-- Place history response display here if needed -->
            </template>
          </HistoryManager>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <!-- Response Display -->
      <v-card variant="outlined" class="pa-3" ref="responseCard">
        <h4 class="text-subtitle-2 mb-2">{{ $t("apiManager.responses") }}</h4>

        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="shapefile">{{ $t("apiManager.shapefile") }}</v-tab>
          <v-tab value="history">{{ $t("apiManager.history") }}</v-tab>
        </v-tabs>

        <v-window v-model="activeTab" class="response-window">
          <v-window-item value="shapefile" key="shapefile">
            <div class="mt-4" :class="{ 'rtl-content': $i18n.locale === 'he' }">
              <v-alert
                v-if="safeShapefileContent"
                type="info"
                variant="tonal"
                class="mb-2"
                :class="{ 'rtl-alert': $i18n.locale === 'he' }"
              >
                <strong>{{ $t("apiManager.shapefileContent") }}</strong
                ><br />
                {{ $t("apiManager.requestId") }}:
                {{ safeShapefileContent.requestId }}<br />
                {{ $t("apiManager.status") }}: {{ safeShapefileContent.status
                }}<br />
                {{ $t("apiManager.version") }}: {{ safeShapefileContent.version
                }}<br />
                {{ $t("apiManager.link") }}:
                <template v-if="safeShapefileContent.link">
                  <a
                    :href="safeShapefileContent.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="word-break: break-all; color: var(--v-theme-primary); text-decoration: underline;"
                  >
                    {{ safeShapefileContent.link }}
                  </a>
                </template>
                <template v-else>
                  {{ safeShapefileContent.link }}
                </template>
              </v-alert>

              <v-alert
                v-if="safeShapefileVersionResponse"
                type="success"
                variant="tonal"
                :class="{ 'rtl-alert': $i18n.locale === 'he' }"
              >
                <strong>{{ $t("apiManager.versionResponse") }}</strong
                ><br />
                {{ $t("apiManager.requestId") }}:
                {{ safeShapefileVersionResponse.requestId }}<br />
                {{ $t("apiManager.status") }}:
                {{ safeShapefileVersionResponse.status }}<br />
                {{ $t("apiManager.version") }}:
                {{ safeShapefileVersionResponse.version }}
              </v-alert>
            </div>
          </v-window-item>

          <v-window-item value="history" key="history">
            <div class="mt-4" :class="{ 'rtl-content': $i18n.locale === 'he' }">
              <v-alert
                v-if="safeHistoryRequest"
                type="info"
                variant="tonal"
                class="mb-2"
                :class="{ 'rtl-alert': $i18n.locale === 'he' }"
              >
                <strong>{{ $t("apiManager.historyRequest") }}</strong
                ><br />
                {{ $t("apiManager.requestId") }}:
                {{ safeHistoryRequest.requestId }}<br />
                {{ $t("apiManager.status") }}: {{ safeHistoryRequest.status }}
              </v-alert>

              <v-alert
                v-if="safeHistoryResponse"
                type="success"
                variant="tonal"
                :class="{ 'rtl-alert': $i18n.locale === 'he' }"
              >
                <strong>{{ $t("apiManager.historyResponse") }}</strong
                ><br />
                {{ $t("apiManager.requestId") }}:
                {{ safeHistoryResponse.requestId }}<br />
                {{ $t("apiManager.status") }}: {{ safeHistoryResponse.status
                }}<br />
                {{ $t("apiManager.link") }}:
                <template v-if="safeHistoryResponse.link">
                  <a
                    :href="safeHistoryResponse.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="word-break: break-all; color: var(--v-theme-primary); text-decoration: underline;"
                  >
                    {{ safeHistoryResponse.link }}
                  </a>
                </template>
                <template v-else>
                  {{ safeHistoryResponse.link }}
                </template>
              </v-alert>
            </div>
          </v-window-item>
        </v-window>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  getDefaultThreatTypes,
  THREAT_CATEGORIES,
  THREAT_EVENTS,
} from "../../config/threatTypes.js";
import DateTimePicker from "./DateTimePicker.vue";
import HistoryManager from "./HistoryManager.vue";
import ShapefileManager from "./ShapefileManager.vue";

export default {
  name: "ApiManager",
  components: {
    DateTimePicker,
    HistoryManager,
    ShapefileManager,
  },
  data: () => ({
    activeTab: "shapefile",
    loadingShapefile: false,
    loadingVersion: false,
    loadingHistory: false,
    shapefileRequestId: "",
    shapefileVersion: "01.01.01",
    historyRequestId: "",
    from: "",
    to: "",
    selectedLayerTypes: ["ALL_IL"],
    selectedCategory: null,
    selectedEvent: null,
  }),
  computed: {
    ...mapGetters([
      "shapefileContent",
      "shapefileVersionResponse",
      "historyRequest",
      "historyResponse",
    ]),
    // Defensive computed properties to prevent recursive updates
    safeShapefileContent() {
      return this.shapefileContent || null;
    },
    safeShapefileVersionResponse() {
      return this.shapefileVersionResponse || null;
    },
    safeHistoryRequest() {
      return this.historyRequest || null;
    },
    safeHistoryResponse() {
      return this.historyResponse || null;
    },
    // Dynamic layer type options based on language
    localizedLayerTypeOptions() {
      // Define the value keys once
      const layerTypes = ["ALL_IL", "CITIES", "SQUARES"];
      // Map to localized titles using $t
      return layerTypes.map((type) => ({
        title: this.$t(`${type}`),
        value: type,
      }));
    },
    categoryOptions() {
      return THREAT_CATEGORIES.map((cat) => ({ title: cat, value: cat }));
    },
    eventOptions() {
      return THREAT_EVENTS.map((ev) => ({ title: ev, value: ev }));
    },
  },
  watch: {
    historyResponse(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.activeTab = "history";
      }
    },
    shapefileContent(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.activeTab = "shapefile";
      }
    },
    shapefileVersionResponse(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.activeTab = "shapefile";
      }
    },
    from(newVal) {
      this.onFromDateOrTimeChanged("from", "date");
    },
    to(newVal) {
      this.onFromDateOrTimeChanged("to", "date");
    },
  },
  methods: {
    ...mapActions([
      "requestShapefileContent",
      "updateShapefileVersion",
      "requestHistoryFromAPI",
    ]),

    async requestShapefileContentHandler() {
      this.loadingShapefile = true;
      try {
        const requestId = this.generateRequestId();
        await this.requestShapefileContent(requestId);
        this.shapefileRequestId = requestId;
        // Switch to shapefile tab to show response
        this.activeTab = "shapefile";
      } catch (error) {
        console.error("Error requesting shapefile content:", error);
      } finally {
        this.loadingShapefile = false;
      }
    },

    async updateShapefileVersionHandler() {
      this.loadingVersion = true;
      try {
        const requestId = this.generateRequestId();
        await this.updateShapefileVersion({
          requestId,
          status: "SUCCESS",
          version: this.shapefileVersion,
        });
        this.shapefileRequestId = requestId;
        // Switch to shapefile tab to show response
        this.activeTab = "shapefile";
      } catch (error) {
        console.error("Error updating shapefile version:", error);
      } finally {
        this.loadingVersion = false;
      }
    },

    async requestHistoryHandler({ from, to, layerTypes, category, event }) {
      this.loadingHistory = true;
      try {
        const requestId = this.generateRequestId();

        const finalFrom =
          from || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const finalTo = to || new Date().toISOString();

        await this.requestHistoryFromAPI({
          requestId,
          from: finalFrom,
          to: finalTo,
          layerTypes,
          // The API requires both fields on a threat type, so send an
          // empty array rather than an entry of nulls when unselected.
          threatTypes: category && event ? [{ category, event }] : [],
        });

        this.historyRequestId = requestId;
        this.activeTab = "history";
      } catch (error) {
        console.error("Error requesting history:", error);
      } finally {
        this.loadingHistory = false;
      }
    },
    generateRequestId() {
      return crypto.randomUUID();
    },
    setFromDateTime() {
      if (this.fromDate && this.fromTime) {
        const dateTime = this.combineDateAndTime(this.fromDate, this.fromTime);
        this.from = this.formatDateTime(dateTime);
        this.fromDisplay = this.from;
      }
      this.fromMenu = false;
      this.fromDatePicked = false;
    },
    setToDateTime() {
      if (this.toDate && this.toTime) {
        const dateTime = this.combineDateAndTime(this.toDate, this.toTime);
        this.to = this.formatDateTime(dateTime);
        this.toDisplay = this.to;
      }
      this.toMenu = false;
      this.toDatePicked = false;
    },
    combineDateAndTime(dateObj, timeStr) {
      if (!(dateObj instanceof Date)) return null;

      const [hours, minutes, seconds = "00"] = timeStr.split(":").map(Number);

      return new Date(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        hours,
        minutes,
        seconds
      );
    },
    formatDateTime(date) {
      if (!(date instanceof Date)) {
        date = new Date(date);
      }

      const pad = (n) => String(n).padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      const offsetMinutes = -date.getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? "+" : "-";
      const offsetHrs = pad(Math.floor(Math.abs(offsetMinutes) / 60));
      const offsetMins = pad(Math.abs(offsetMinutes) % 60);
      const offset = `${sign}${offsetHrs}:${offsetMins}`;

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
    },
    onFromDatePicked() {
      this.fromDatePicked = true;
      // Do not close menu here
    },
    onFromTimePicked() {
      if (this.fromDate && this.fromTime) {
        const dateTime = this.combineDateAndTime(this.fromDate, this.fromTime);
        this.from = this.formatDateTime(dateTime);
        this.fromDisplay = this.from;
      }
      this.fromMenu = false;
    },
    onToDatePicked() {
      this.toDatePicked = true;
      // Do not close menu here
    },
    onToTimePicked() {
      if (this.toDate && this.toTime) {
        const dateTime = this.combineDateAndTime(this.toDate, this.toTime);
        this.to = this.formatDateTime(dateTime);
        this.toDisplay = this.to;
      }
      this.toMenu = false;
    },
    onFromDateOrTimeChanged(which, changedPart) {
      let currentDateTime;
      // Parse current combined value if exists
      if (this[which]) {
        currentDateTime = new Date(this[which]);
        if (isNaN(currentDateTime)) {
          currentDateTime = new Date(); // fallback to now if invalid
        }
      } else {
        currentDateTime = new Date(); // fallback to now
      }

      if (changedPart === "date" && this[`${which}Date`]) {
        const newDate = this[`${which}Date`];
        currentDateTime.setFullYear(newDate.getFullYear());
        currentDateTime.setMonth(newDate.getMonth());
        currentDateTime.setDate(newDate.getDate());
      }

      if (changedPart === "time" && this[`${which}Time`]) {
        const [hours, minutes] = this[`${which}Time`].split(":");
        currentDateTime.setHours(Number(hours), Number(minutes), 0, 0);
      }

      this[which] = this.formatDateTime(currentDateTime);
      this[`${which}Display`] = this[which];
    },
    formatDateOnly(date) {
      if (!(date instanceof Date)) date = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`;
    },
    formatTimeOnly(time) {
      // time is a string like HH:mm or HH:mm:ss
      return time.length === 5 ? `${time}:00` : time;
    },
  },
  mounted() {
    // Set default dates
    this.from = new Date(Date.now() - 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16);
    this.to = new Date().toISOString().slice(0, 16);
    this.fromDisplay = this.formatDateTime(this.from);
    this.toDisplay = this.formatDateTime(this.to);
  },
};
</script>

<style scoped>
.api-manager, .v-card, .v-container, .v-row, .v-col {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* RTL support for response content */
.rtl-content {
  direction: rtl;
  text-align: right;
}

.rtl-alert {
  direction: rtl;
  text-align: right;
}

.rtl-alert strong {
  display: block;
  margin-bottom: 8px;
}

.rtl-alert br {
  display: block;
  content: "";
  margin: 4px 0;
}

/* Ensure proper spacing in RTL */
.rtl-content .v-alert {
  text-align: right;
}

.rtl-content .v-alert__content {
  direction: rtl;
}

/* Override Vuetify alert styles for RTL */
.rtl-alert .v-alert__content {
  direction: rtl !important;
  text-align: right !important;
}

.rtl-alert .v-alert__wrapper {
  direction: rtl !important;
}

/* Ensure proper text direction for RTL */
[dir="rtl"] .rtl-content,
[dir="rtl"] .rtl-alert {
  direction: rtl !important;
  text-align: right !important;
}

/* Fix for Vuetify tabs in RTL */
[dir="rtl"] .v-tabs {
  direction: rtl;
}

[dir="rtl"] .v-tab {
  direction: rtl;
  text-align: right;
}

/* Fix for Vuetify cards in RTL */
[dir="rtl"] .v-card {
  direction: rtl;
}

[dir="rtl"] .v-card-text {
  direction: rtl;
  text-align: right;
}

/* RTL support for buttons */
.rtl-btn {
  direction: rtl;
  text-align: right;
}

.rtl-btn .v-btn__content {
  direction: rtl;
  flex-direction: row-reverse;
}

/* RTL support for input fields */
.rtl-input {
  direction: rtl;
  text-align: right;
}

.rtl-input .v-field__input {
  direction: rtl;
  text-align: right;
}

.rtl-input .v-field__field {
  direction: rtl;
}

/* RTL support for select fields */
.rtl-input .v-select__selection {
  direction: rtl;
  text-align: right;
}

/* Ensure proper spacing for RTL elements */
[dir="rtl"] .v-btn {
  direction: rtl;
}

[dir="rtl"] .v-text-field {
  direction: rtl;
}

[dir="rtl"] .v-select {
  direction: rtl;
}

/* Style for readonly fields */
.v-text-field[readonly] {
  background-color: rgba(0, 0, 0, 0.04);
}

.v-theme--dark .v-text-field[readonly] {
  background-color: rgba(255, 255, 255, 0.04);
}

.v-text-field[readonly] .v-field__input {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-style: italic;
}

/* RTL support for readonly fields */
.rtl-input[readonly] {
  background-color: rgba(0, 0, 0, 0.04);
}

.v-theme--dark .rtl-input[readonly] {
  background-color: rgba(255, 255, 255, 0.04);
}

/* Request ID styling */
.request-id {
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  padding: 4px 8px;
  border-radius: 4px;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.9em;
  font-weight: 600;
  display: inline-block;
  margin-left: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.v-theme--dark .request-id {
  background-color: rgba(var(--v-theme-primary), 0.2);
  border-color: rgba(var(--v-theme-primary), 0.4);
}

/* RTL support for request ID */
.rtl-alert .request-id {
  margin-left: 0;
  margin-right: 8px;
}

/* Smooth transition for response window */
.response-window {
  transition: all 0.3s ease-in-out;
}

.response-window .v-window-item {
  transition: opacity 0.3s ease-in-out;
}

/* Highlight active tab when switching */
.v-tab--selected {
  font-weight: 600;
}

/* Smooth tab switching animation */
.v-tabs .v-tab {
  transition: all 0.2s ease-in-out;
}

@media (max-width: 768px) {
  .api-manager, .v-card, .v-container, .v-row, .v-col {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    font-size: 90% !important;
  }
  .v-select, .v-text-field {
    font-size: 0.75em !important;
    min-height: 28px !important;
    padding: 0.2em 0.5em !important;
  }

}
</style>
<style>
  @media (max-width: 768px) {
    .api-manager .v-btn, .api-manager .v-input {
      min-height: 20% !important;
      max-width: 90% !important;
      min-width: 90% !important;
    }

    .response-window {
      margin: 5% !important;
    }
  }
</style>
